// Space view: a standalone Three.js scene showing Earth, the Sun, the Moon, and
// tracked satellites/telescopes at real (schematically-scaled) positions — the
// "zoom out past the planet" view. Deliberately a separate canvas rather than a
// MapLibre CustomLayerInterface: MapLibre's own docs note that a plain
// projection-matrix custom layer is only reliable under mercator projection,
// and globe-projection custom layers are a newer, less-proven API surface not
// worth betting an untestable 3D composite on. This overlay fades in/out based
// on map zoom instead, which is simple, robust, and independently testable.
import * as THREE from 'three';

const EARTH_RADIUS_SCENE = 5; // arbitrary scene units
const EARTH_RADIUS_KM = 6371;
const MOON_DISTANCE_SCENE = 13; // schematic — not to scale (real ratio would be ~60x Earth radii)
const SUN_DISTANCE_SCENE = 30; // schematic — the sun is placed for correct *direction*, not distance
const SATELLITE_ORBIT_SCENE = EARTH_RADIUS_SCENE * 1.35; // exaggerated for visibility, same idea as
// the main map's schematic satellite altitude — real LEO orbits (~300-600km) would sit almost on
// the sphere's surface at this scale and be invisible.

export function createSpaceView(canvas) {
  const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));

  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(45, 1, 0.1, 1000);
  camera.position.set(0, 6, 22);
  camera.lookAt(0, 0, 0);

  scene.add(new THREE.AmbientLight(0x223344, 1.2));

  const earth = new THREE.Mesh(
    new THREE.SphereGeometry(EARTH_RADIUS_SCENE, 48, 48),
    new THREE.MeshStandardMaterial({ color: 0x0a1420, emissive: 0x061018, roughness: 1 }),
  );
  scene.add(earth);

  const atmosphere = new THREE.Mesh(
    new THREE.SphereGeometry(EARTH_RADIUS_SCENE * 1.03, 48, 48),
    new THREE.MeshBasicMaterial({ color: 0x00e5ff, transparent: true, opacity: 0.08, side: THREE.BackSide }),
  );
  scene.add(atmosphere);

  const sunLight = new THREE.PointLight(0xfff4e0, 2.2, 0, 0);
  const sunMesh = new THREE.Mesh(
    new THREE.SphereGeometry(1.4, 16, 16),
    new THREE.MeshBasicMaterial({ color: 0xffd166 }),
  );
  scene.add(sunLight, sunMesh);

  const moonMesh = new THREE.Mesh(
    new THREE.SphereGeometry(0.55, 16, 16),
    new THREE.MeshStandardMaterial({ color: 0xaaaaaa, roughness: 1 }),
  );
  scene.add(moonMesh);

  const satelliteGroup = new THREE.Group();
  scene.add(satelliteGroup);

  function resize() {
    const w = canvas.clientWidth || 1;
    const h = canvas.clientHeight || 1;
    renderer.setSize(w, h, false);
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
  }

  let angle = 0;

  function render() {
    angle += 0.0015; // a slow, purely cosmetic auto-rotate so the scene doesn't feel static
    const r = 22;
    camera.position.set(Math.sin(angle) * r, 6, Math.cos(angle) * r);
    camera.lookAt(0, 0, 0);
    renderer.render(scene, camera);
  }

  // ecfToScene: real Earth-fixed km -> scene units, using Earth's own radius as the
  // reference scale (direction is preserved exactly; only distance is schematic for
  // bodies placed at a fixed display distance below).
  function ecfDirection(ecf) {
    const len = Math.hypot(ecf.x, ecf.y, ecf.z) || 1;
    // Scene axes: scene Y = ECF Z (Earth's rotation axis), matching a conventional
    // "north up" globe. This is an arbitrary but fixed convention used consistently
    // for the sun, moon, and satellites below, so their relative directions agree.
    return { x: ecf.x / len, y: ecf.z / len, z: ecf.y / len };
  }

  function update({ sunEcf, moonEcf, satellites }) {
    const sunDir = ecfDirection(sunEcf);
    sunMesh.position.set(sunDir.x * SUN_DISTANCE_SCENE, sunDir.y * SUN_DISTANCE_SCENE, sunDir.z * SUN_DISTANCE_SCENE);
    sunLight.position.copy(sunMesh.position);

    const moonDir = ecfDirection(moonEcf);
    moonMesh.position.set(
      moonDir.x * MOON_DISTANCE_SCENE,
      moonDir.y * MOON_DISTANCE_SCENE,
      moonDir.z * MOON_DISTANCE_SCENE,
    );

    while (satelliteGroup.children.length > satellites.length) {
      satelliteGroup.remove(satelliteGroup.children[satelliteGroup.children.length - 1]);
    }
    satellites.forEach((sat, i) => {
      let dot = satelliteGroup.children[i];
      if (!dot) {
        dot = new THREE.Mesh(
          new THREE.SphereGeometry(0.12, 8, 8),
          new THREE.MeshBasicMaterial({ color: 0x7dffb2 }),
        );
        satelliteGroup.add(dot);
      }
      dot.material.color.set(sat.kind === 'telescope' ? 0xc792ff : 0x7dffb2);
      const dir = ecfDirection(sat.ecf);
      dot.position.set(
        dir.x * SATELLITE_ORBIT_SCENE,
        dir.y * SATELLITE_ORBIT_SCENE,
        dir.z * SATELLITE_ORBIT_SCENE,
      );
    });
  }

  let rafId = null;
  function loop() {
    render();
    rafId = requestAnimationFrame(loop);
  }
  function start() {
    resize();
    if (rafId == null) loop();
  }
  function stop() {
    if (rafId != null) cancelAnimationFrame(rafId);
    rafId = null;
  }

  window.addEventListener('resize', resize);

  return { update, start, stop, resize };
}
